/**
 * Functions that handle resizing of columns in a table.
 * Adds two listeners to the table:
 * 1. Resize listener that resizes the column to the width of the table if the column width exceeds the table width.
 *
 */

import { useEffect } from 'react';
import ResizerIcon from 'app/common/utils/resizer/ResizerIcon';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';

export function useResizeListener() {
  const location = useLocation();
  useEffect(() => {
    const handleLocationChange = () => {
      addResizer();
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);

    // Call addResizer initially when the component mounts
    addResizer();

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, [location]);
}

function addResizer() {
  // interval needed here because the table is rendered after the component is mounted
  const intervalId = setInterval(() => {
    const table = document.querySelector('.SelectListGrid');
    if (!table) {
      return;
    }
    clearInterval(intervalId);
    const headers = table.querySelectorAll('th.ListGridHeader');

    headers.forEach((header, i) => {
      // Skip the last header. No need to add a resizer to the last column
      if (i === headers.length - 1) {
        return;
      }
      // Already exists. Prevents multiple resizers from being added
      if (header.querySelector('.ColumnResizer')) {
        return;
      }
      const resizer = buildResizerDiv(header);
      header.style.position = 'relative';
      let startX, startWidth;

      // Adjust the width of the column when the resizer is dragged
      const onMouseMove = e => {
        const newWidth = startWidth + (e.pageX - startX);
        header.style.width = `${newWidth}px`;
      };

      // Same as above but for touch events
      const onTouchMove = e => {
        const newWidth = startWidth + (e.touches[0].pageX - startX);
        header.style.width = `${newWidth}px`;
      };
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      const onTouchEnd = () => {
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      resizer.addEventListener('mousedown', e => {
        if (e.button !== 0) {
          return;
        }
        startX = e.pageX;
        startWidth = header.offsetWidth;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      resizer.addEventListener('touchstart', e => {
        startX = e.touches[0].pageX;
        startWidth = header.offsetWidth;
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
      });

      // Enforces the table size does not exceed the parent container
      enforceTableSize(header, table);
    });
  }, 100);

  return () => clearInterval(intervalId);
}

function buildResizerDiv(header) {
  const resizerClassList = [
    'ColumnResizer',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'hover:tw-no-underline',
    'tw-text-gray-800',
    'hover:tw-text-gray-900',
    'tw-text-gray-900'
  ];

  const sortLink = header.querySelector('a.ActionLink');
  const resizer = document.createElement('div');
  resizer.classList.add(...resizerClassList);
  if (sortLink) {
    const contentParent = document.createElement('div');
    contentParent.classList.add('tw-flex', 'tw-flex-row');

    sortLink.classList.add('tw-col-span-5');
    resizer.classList.add('tw-col-span-1');

    contentParent.appendChild(sortLink);
    contentParent.appendChild(resizer);
    header.appendChild(contentParent);
  } else {
    header.appendChild(resizer);
  }

  resizer.style.width = '20px';
  resizer.style.height = '100%';
  resizer.style.position = 'absolute';
  resizer.style.right = '0';
  resizer.style.top = '0';
  resizer.style.cursor = 'col-resize';
  resizer.style.userSelect = 'none';

  addResizerIcon(resizer);

  return resizer;
}

function enforceTableSize(header, table) {
  header.addEventListener('mouseup', () => {
    const tableWidth = table.offsetWidth;
    const headerWidth = header.offsetWidth;

    if (headerWidth > tableWidth) {
      header.style.width = `${tableWidth}px`;
    }
  });
}

function addResizerIcon(resizer) {
  const iconContainer = document.createElement('div');
  ReactDOM.render(<ResizerIcon />, iconContainer);
  resizer.appendChild(iconContainer);
}
